import type { WeekdayNumbers } from "luxon";
import { DateTime } from "luxon";
import type {
  TimelineColumn,
  TimelineData,
  TimelineItem,
  TimelineShift,
} from "@/lib/components/timeline";

// Create generic week columns (Monday to Sunday, no real dates)
export function createWeekColumns(): TimelineColumn[] {
  const dayNames = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  return dayNames.map((dayName, _index) => ({
    date: DateTime.invalid("generic week column"), // Invalid date to prevent showing concrete dates
    label: dayName.slice(0, 3), // Mon, Tue, Wed, etc.
    isToday: false,
    isPast: false,
  }));
}

// Create columns with worker name as first column, then days
export function createWorkerTimelineColumns(): TimelineColumn[] {
  const workerColumn: TimelineColumn = {
    date: DateTime.fromObject({ weekday: 0 as WeekdayNumbers }), // Special case for worker name column
    label: "Worker",
    isToday: false,
    isPast: false,
  };

  const dayColumns = createWeekColumns();

  return [workerColumn, ...dayColumns];
}

// Generate timeline data for a worker plan
export function generateTimelineData(
  days: number[],
  timeIntervals: Array<{ startTime: string; endTime: string; sortOrder: number }>,
  color: string,
  _scheduleType: string
): TimelineData[] {
  const weekColumns = createWeekColumns();

  return weekColumns.map((_column, index) => {
    const dayOfWeek = index + 1; // 1-7 for Mon-Sun
    const isWorkingDay = days.includes(dayOfWeek);

    if (!isWorkingDay || !timeIntervals.length) {
      return {
        date: DateTime.invalid("generic week column"), // Invalid date to hide concrete dates
        isEmpty: true,
      };
    }

    // For multiple intervals, create a combined shift showing the full day span
    const sortedIntervals = [...timeIntervals].sort((a, b) => a.sortOrder - b.sortOrder);
    const firstInterval = sortedIntervals[0];
    const lastInterval = sortedIntervals[sortedIntervals.length - 1];

    const shift: TimelineShift = {
      id: `shift-${dayOfWeek}`,
      startTime: firstInterval.startTime,
      endTime: lastInterval.endTime,
      color: color,
      label:
        timeIntervals.length > 1
          ? `${timeIntervals.length} intervals`
          : `${firstInterval.startTime}-${lastInterval.endTime}`,
      subtitle:
        timeIntervals.length > 1 ? `${firstInterval.startTime}-${lastInterval.endTime}` : undefined,
    };

    return {
      date: DateTime.invalid("generic week column"), // Invalid date to hide concrete dates
      shift,
    };
  });
}

// Generate timeline data with worker name in first column
export function generateWorkerTimelineData(
  days: number[],
  timeIntervals: Array<{ startTime: string; endTime: string; sortOrder: number }>,
  color: string,
  _scheduleType: string,
  workerName: string
): TimelineData[] {
  const workerTimelineColumns = createWorkerTimelineColumns();

  return workerTimelineColumns.map((_column, index) => {
    // First column is for worker name
    if (index === 0) {
      return {
        date: DateTime.invalid("generic week column"), // Invalid date to hide concrete dates
        workerName: workerName, // Store worker name for first column
        isEmpty: true, // Don't show shifts in worker name column
      };
    }

    // Other columns are for days (index - 1 because first column is worker name)
    const dayOfWeek = index; // 1-7 for Mon-Sun (adjusted for worker column)
    const isWorkingDay = days.includes(dayOfWeek);

    if (!isWorkingDay || !timeIntervals.length) {
      return {
        date: DateTime.invalid("generic week column"), // Invalid date to hide concrete dates
        isEmpty: true,
      };
    }

    // For multiple intervals, create a combined shift showing the full day span
    const sortedIntervals = [...timeIntervals].sort((a, b) => a.sortOrder - b.sortOrder);
    const firstInterval = sortedIntervals[0];
    const lastInterval = sortedIntervals[sortedIntervals.length - 1];

    const shift: TimelineShift = {
      id: `shift-${dayOfWeek}`,
      startTime: firstInterval.startTime,
      endTime: lastInterval.endTime,
      color: color,
      label:
        timeIntervals.length > 1
          ? `${timeIntervals.length} intervals`
          : `${firstInterval.startTime}-${lastInterval.endTime}`,
      subtitle:
        timeIntervals.length > 1 ? `${firstInterval.startTime}-${lastInterval.endTime}` : undefined,
    };

    return {
      date: DateTime.invalid("generic week column"), // Invalid date to hide concrete dates
      shift,
    };
  });
}

// Transform worker plan to timeline format
export function transformPlanToTimeline(plan: any, worker: any) {
  const scheduleType =
    plan.planType === "TEMPLATE" ? plan.template?.scheduleType : plan.customScheduleType;

  const days = plan.planType === "TEMPLATE" ? plan.template?.days || [] : plan.customDays || [];

  const timeIntervals =
    plan.planType === "TEMPLATE"
      ? plan.template?.timeIntervals || []
      : plan.customTimeIntervals || [];

  const color = plan.template?.color || worker.color || "#3b82f6";

  const weekColumns = createWeekColumns();

  const baseTimelineData = generateTimelineData(
    days,
    timeIntervals,
    color,
    scheduleType || "weekly"
  );

  const timelineItem: TimelineItem = {
    id: worker.id,
    name: `${worker.user?.firstName || ""} ${worker.user?.lastName || ""}`.trim() || "Worker",
    color: color,
    data: baseTimelineData,
  };

  return {
    items: [timelineItem],
    columns: weekColumns,
    showTwoWeeks: scheduleType?.includes("biweekly") || false,
    scheduleType: scheduleType || "weekly",
  };
}

// Transform worker plan to timeline format with worker name in first column
export function transformPlanToWorkerTimeline(plan: any, worker: any) {
  const scheduleType =
    plan.planType === "TEMPLATE" ? plan.template?.scheduleType : plan.customScheduleType;

  const days = plan.planType === "TEMPLATE" ? plan.template?.days || [] : plan.customDays || [];

  const timeIntervals =
    plan.planType === "TEMPLATE"
      ? plan.template?.timeIntervals || []
      : plan.customTimeIntervals || [];

  const color = plan.template?.color || worker.color || "#3b82f6";
  const workerName =
    `${worker.user?.firstName || ""} ${worker.user?.lastName || ""}`.trim() || "Worker";

  // Use regular week columns for the timeline data
  const baseTimelineData = generateTimelineData(
    days,
    timeIntervals,
    color,
    scheduleType || "weekly"
  );

  const timelineItem: TimelineItem = {
    id: worker.id,
    name: workerName, // Keep worker name in item.name for Timeline component
    color: color,
    data: baseTimelineData,
  };

  return {
    items: [timelineItem],
    columns: createWeekColumns(), // Use regular columns - the worker name will be in the left sidebar
    showTwoWeeks: scheduleType?.includes("biweekly") || false,
    scheduleType: scheduleType || "weekly",
  };
}

// Get schedule type label for display
export function getScheduleTypeLabel(scheduleType: string): string {
  switch (scheduleType) {
    case "weekly":
      return "Weekly Schedule";
    case "biweekly_even":
      return "Bi-weekly (Even Weeks)";
    case "biweekly_odd":
      return "Bi-weekly (Odd Weeks)";
    default:
      return "Schedule";
  }
}

// Generate timeline data for even/odd weeks display
export function generateBiweeklyTimeline(plan: any, worker: any) {
  const baseResult = transformPlanToTimeline(plan, worker);

  if (!baseResult.showTwoWeeks) {
    return baseResult;
  }

  const scheduleType =
    plan.planType === "TEMPLATE" ? plan.template?.scheduleType : plan.customScheduleType;

  // For biweekly schedules, show which week is active
  const isEvenWeek = scheduleType === "biweekly_even";
  const isOddWeek = scheduleType === "biweekly_odd";

  // Create two timeline items - one for even weeks, one for odd weeks
  const evenWeekItem: TimelineItem = {
    ...baseResult.items[0],
    id: `${worker.id}-even`,
    name: "Even Weeks",
    subtitle: "(Weeks 2, 4, 6, 8...)",
    data: isEvenWeek
      ? baseResult.items[0].data
      : baseResult.items[0].data.map((d) => ({ ...d, isEmpty: true, shift: undefined })),
  };

  const oddWeekItem: TimelineItem = {
    ...baseResult.items[0],
    id: `${worker.id}-odd`,
    name: "Odd Weeks",
    subtitle: "(Weeks 1, 3, 5, 7...)",
    data: isOddWeek
      ? baseResult.items[0].data
      : baseResult.items[0].data.map((d) => ({ ...d, isEmpty: true, shift: undefined })),
  };

  return {
    ...baseResult,
    items: [evenWeekItem, oddWeekItem],
  };
}

// Generate biweekly timeline with worker names in first column
export function generateBiweeklyWorkerTimeline(plan: any, worker: any) {
  const baseResult = transformPlanToWorkerTimeline(plan, worker);

  if (!baseResult.showTwoWeeks) {
    return baseResult;
  }

  const scheduleType =
    plan.planType === "TEMPLATE" ? plan.template?.scheduleType : plan.customScheduleType;

  // For biweekly schedules, show which week is active
  const isEvenWeek = scheduleType === "biweekly_even";
  const isOddWeek = scheduleType === "biweekly_odd";
  const workerName =
    `${worker.user?.firstName || ""} ${worker.user?.lastName || ""}`.trim() || "Worker";

  // Create two timeline items - one for even weeks, one for odd weeks
  const evenWeekItem: TimelineItem = {
    ...baseResult.items[0],
    id: `${worker.id}-even`,
    name: workerName, // Keep worker name for Timeline component
    data: isEvenWeek
      ? baseResult.items[0].data
      : baseResult.items[0].data.map((d) => ({ ...d, isEmpty: true, shift: undefined })),
  };

  const oddWeekItem: TimelineItem = {
    ...baseResult.items[0],
    id: `${worker.id}-odd`,
    name: workerName, // Keep worker name for Timeline component
    data: isOddWeek
      ? baseResult.items[0].data
      : baseResult.items[0].data.map((d) => ({ ...d, isEmpty: true, shift: undefined })),
  };

  return {
    ...baseResult,
    items: [evenWeekItem, oddWeekItem],
  };
}
